export class VoxelWorld {
  static OP_SET_BIT = 1;
  static OP_SET_COLOR = 2;
  static OP_NEW_CHUNK = 3;
  static OP_SET_META = 4;

  constructor(instance, exports) {
    this.instance = instance;
    this.exports = exports;

    this.world_create = exports.world_create;
    this.world_destroy = exports.world_destroy;
    this.world_add_chunk = exports.world_add_chunk;
    this.world_add_voxel = exports.world_add_voxel;
    this.world_buffer = exports.world_buffer;
    this.world_buffer_size = exports.world_buffer_size;
    this.world_buffer_reset = exports.world_buffer_reset;

    this.memory = exports.memory;

    this.destroyed = false;
  }

  static async create(wasmPath, N, maxChunks, importObject = {}) {
    const bytes = await fetch(wasmPath).then(r => r.arrayBuffer());

    const { instance } = await WebAssembly.instantiate(bytes, {
      env: {
        abort: () => {
          throw new Error("wasm abort");
        },
        ...importObject.env,
      },
      ...importObject,
    });

    const exports = instance.exports;

    const world = new VoxelWorld(instance, exports);

    world.world = world.world_create(N, maxChunks);

    return world;
  }

  addVoxel(x, y, z, color) {
    this.world_add_voxel(this.world, x, y, z, color);
  }

  buffer() {
    const ptr = this.world_buffer(this.world);
    const size = this.world_buffer_size(this.world);

    return new Uint8Array(this.memory.buffer, ptr, size);
  }

  *commands() {
    const data = this.buffer();

    let offset = 0;

    while (offset < data.length) {
      const opcode = data[offset++];

      if (opcode === VoxelWorld.OP_SET_META) {
        const view = new DataView(data.buffer, data.byteOffset + offset, 8);
        const N = view.getUint32(0, true);
        const maxChunks = view.getUint32(4, true);

        const chunkTextureWidth = view.getUint32(4, true);
        const chunkTextureHeight = view.getUint32(4, true);
        const chunkTextureDepth = view.getUint32(4, true);

        const bufferWidth = view.getUint32(4, true);
        const bufferHeight = view.getUint32(4, true);
        const bufferDepth = view.getUint32(4, true);
        offset += 32;

        yield {
          op: "SET_META",
          N,
          maxChunks,
          chunkTextureWidth,
          chunkTextureHeight,
          chunkTextureDepth,
          bufferWidth,
          bufferHeight,
          bufferDepth
        };

      } else if (opcode === VoxelWorld.OP_NEW_CHUNK) {
        const view = new DataView(data.buffer, data.byteOffset + offset, 32);

        const key = view.getUint32(0, true);
        const index = view.getUint32(4, true);
        const x = view.getInt32(8, true);
        const y = view.getInt32(12, true);
        const z = view.getInt32(16, true);
        const tx = view.getInt32(20, true);
        const ty = view.getInt32(24, true);
        const tz = view.getInt32(28, true);

        offset += 32;

        yield {
          op: "NEW_CHUNK",
          key,
          index,
          x,
          y,
          z,
          tx,
          ty,
          tz,
        };

      } else if (opcode === VoxelWorld.OP_SET_BIT) {
        const view = new DataView(data.buffer, data.byteOffset + offset, 9);

        const chunkIndex = view.getUint32(0, true);
        const voxelIndex = view.getUint32(4, true);
        const bit = view.getUint8(8);

        offset += 9;

        yield {
          op: "SET_BIT",
          chunkIndex,
          voxelIndex,
          bit,
        };

      } else if (opcode === VoxelWorld.OP_SET_COLOR) {
        const view = new DataView(data.buffer, data.byteOffset + offset, 9);

        const chunkIndex = view.getUint32(0, true);
        const colorIndex = view.getUint32(4, true);
        const color = view.getUint8(8);

        offset += 9;

        yield {
          op: "SET_COLOR",
          chunkIndex,
          colorIndex,
          color,
        };

      } else {
        throw new Error(`Unknown opcode ${opcode}`);
      }
    }
    this.world_buffer_reset(this.world);
  }

  addChunk(cx, cy, cz, tx, ty, tz, index) {
    this.world_add_chunk(this.world, cx, cy, cz, tx, ty, tz, index);
  }

  destroy() {
    if (!this.destroyed) {
      this.world_destroy(this.world);
      this.destroyed = true;
    }
  }

  close() {
    this.destroy();
  }
}
